import React from 'react';
import { Link } from 'react-router-dom';
import Footer from '../../components/layout/Footer';
import WiwInvestigationHeader from '../../components/layout/WiwInvestigationHeader';

/**
 * Shared shell for legal / marketing informational pages.
 *
 * ``html`` / ``#root`` lock overflow for the Home workspace; this shell is the
 * scroll container so long policies remain readable and the footer stays reachable.
 *
 * @param {{ title: string, path?: string, children: React.ReactNode }} props
 */
export default function LegalPageShell({ title, children }) {
  return (
    <div
      className="legal-page-shell relative flex h-[100dvh] max-h-[100dvh] min-h-0 w-full flex-col overflow-x-hidden overflow-y-auto overscroll-y-contain bg-[#0f172a] text-slate-100"
      data-testid="legal-page-shell"
    >
      <div className="sticky top-0 z-40 flex shrink-0 items-stretch border-b border-white/10 bg-[#0f172a]/95 backdrop-blur-xl">
        <div className="min-w-0 flex-1 [&_.wiw-inv-header]:static [&_.wiw-inv-header]:border-0 [&_.wiw-inv-header]:bg-transparent [&_.wiw-inv-header]:backdrop-blur-none">
          <WiwInvestigationHeader />
        </div>
        <div className="flex shrink-0 items-center pr-3 pt-[max(0.25rem,env(safe-area-inset-top))] sm:pr-6">
          <Link
            to="/"
            className="rounded-lg border border-white/15 bg-white/5 px-3 py-1.5 text-xs font-semibold text-slate-100 transition hover:border-cyan-400/40 hover:bg-cyan-500/10 hover:text-cyan-100 sm:px-4 sm:text-sm"
            data-testid="legal-page-close"
          >
            Close
          </Link>
        </div>
      </div>

      <main className="mx-auto w-full max-w-3xl flex-1 px-4 py-8 sm:px-6 sm:py-10">
        <p className="mb-2 font-mono text-[10px] uppercase tracking-[0.2em] text-cyan-500/70">
          CyberTracer.ro
        </p>
        <h1 className="mb-6 font-['Orbitron',ui-sans-serif,sans-serif] text-2xl font-black tracking-wide text-white sm:text-3xl">
          {title}
        </h1>
        <article className="legal-prose space-y-4 text-sm leading-relaxed text-slate-300 [&_a]:text-cyan-300 [&_a]:underline [&_h2]:mt-6 [&_h2]:text-base [&_h2]:font-bold [&_h2]:text-slate-100 [&_li]:ml-4 [&_li]:list-disc [&_strong]:text-slate-100 [&_ul]:space-y-1">
          {children}
        </article>
        <p className="mt-10 text-xs text-slate-500">
          <Link to="/" className="text-cyan-400 hover:text-cyan-300">
            ← Back to workspace
          </Link>
          {' · '}
          <Link to="/about" className="text-cyan-400 hover:text-cyan-300">
            About
          </Link>
          {' · '}
          <Link to="/privacy" className="text-cyan-400 hover:text-cyan-300">
            Privacy
          </Link>
          {' · '}
          <Link to="/cookies" className="text-cyan-400 hover:text-cyan-300">
            Cookies
          </Link>
          {' · '}
          <Link to="/terms" className="text-cyan-400 hover:text-cyan-300">
            Terms
          </Link>
        </p>
      </main>
      <Footer className="relative z-20 w-full shrink-0" />
    </div>
  );
}
